// Inside-the-Canvas half of the minimap's camera view box (minimap-design.md §4):
// unprojects the four NDC corners onto the ground plane and publishes the footprint
// polygon to `cameraView.ts` at ~15 Hz, change-only, so the minimap can stroke it.
//
// It also publishes the camera's ground FOCUS — the orbit target, i.e. the point the
// camera actually looks at. The polygon alone cannot give that: a tilted camera's
// footprint is a long wedge, so its centroid sits well beyond where the camera is.
//
// Both are published in BOARD coordinates (the Large Board render scale divided
// out) — the same space the minimap's unit math uses, so no consumer needs to know the
// scale. Scene mounts this next to CameraFlight / CameraPanClamp.

import { useFrame } from '@react-three/fiber';
import { type RefObject, useMemo, useRef } from 'react';
import { Raycaster, Vector2 } from 'three';
import type { OrbitControlsLike } from '../../cinematics/CinematicCameraRig.js';
import { getBoardScale } from '../boardScale.js';
import { type Ray, footprintPolygon } from './cameraFootprint.js';
import { useCameraView } from './cameraView.js';

/** Corner rays longer than this are clipped (a near-horizontal camera would run to the horizon). */
export const FAR_CLIP = 120;

const CORNERS = [
  [-1, -1],
  [1, -1],
  [1, 1],
  [-1, 1],
] as const;

const PUBLISH_INTERVAL_MS = 1000 / 15;

export function CameraViewPublisher({
  controls,
}: {
  readonly controls: RefObject<OrbitControlsLike | null>;
}) {
  const raycaster = useMemo(() => new Raycaster(), []);
  const ndc = useMemo(() => new Vector2(), []);
  const lastPublish = useRef(0);

  useFrame(({ camera }, delta) => {
    // 15 Hz is plenty for a view box that follows an eased orbit; skip early frames.
    lastPublish.current += delta * 1000;
    if (lastPublish.current < PUBLISH_INTERVAL_MS) return;
    lastPublish.current = 0;
    const pts: Ray[] = [];
    for (const [x, y] of CORNERS) {
      ndc.set(x, y);
      raycaster.setFromCamera(ndc, camera);
      const o = raycaster.ray.origin;
      const d = raycaster.ray.direction;
      pts.push({ ox: o.x, oy: o.y, oz: o.z, dx: d.x, dy: d.y, dz: d.z });
    }
    const scale = getBoardScale();
    const poly = footprintPolygon(pts, FAR_CLIP * scale).map(
      ([x, z]) => [x / scale, z / scale] as const,
    );
    // The orbit target is in rendered space (the board group is scaled), so the
    // same division as the polygon puts it back into board coordinates. The
    // controls ref is null until OrbitControls mounts — publish no focus then.
    const target = controls.current?.target;
    const focus = target ? { x: target.x / scale, y: target.z / scale } : null;
    useCameraView.getState().publish(poly, focus);
  });

  return null;
}
